import React, { useLayoutEffect, useMemo } from "react";

import {
	SMSTemplate,
	Order,
	Tariff,
	ExecutorLocations,
	Car,
} from "../../services";
import { useTypedDispatch, useTypedSelector } from "../../redux/store";
import orderPage from "../../redux/reducers/OrdersPage";
import setRates from "../../redux/actions/Preferences/Rates/setRates";
import { globalState } from "../../redux/reducers";
import getPolygons from "../../redux/services/Map/polygons";
import { ordersAction as orders } from "../../redux/reducers/Orders";
import { MessageTemplateActions } from "../../types/MessageTemplateEnums";
import { useModelSubscribe } from "../../hooks";
import { useTaxiServiceFilterAccess } from "../../access";

import { OrdersDataLoader, ChatMessageLoader } from "./components";

const ORDER_SMS_TEMPLATES = [
	MessageTemplateActions.WILL_BE_TIME,
	MessageTemplateActions.OUTPUT_PASSENGERS,
	MessageTemplateActions.LATE,
	MessageTemplateActions.CAR_NOT_FOUND,
	MessageTemplateActions.ORDER_SUCCESSFULLY_COMPLETED,
	MessageTemplateActions.WITH_PASSENGERS,
	MessageTemplateActions.DRIVER_SWITCHED,
];

const OrderPageModelsLoader: React.FC<OrderPageModelsLoader.Props> = () => {
	const dispatch = useTypedDispatch();

	const { accessTaxiServicesIds } = useTaxiServiceFilterAccess<Order.Model>();

	const { connected, authorized } = useTypedSelector((state) => state.prpc);
	const lang = useTypedSelector((state) => state.session.language);

	const ratesSubscription = useModelSubscribe({}, Tariff);
	const optionsCarService = useMemo<Car.SubscribeOptions>(() => {
		const result: Car.SubscribeOptions = {
			language: lang,
			taxiServiceIds: accessTaxiServicesIds,
		};

		return result;
	}, [accessTaxiServicesIds, lang]);

	const { models: CarSub } = useModelSubscribe(optionsCarService, Car);
	const smsTemplatesSubscription = useModelSubscribe(
		{
			actions: ORDER_SMS_TEMPLATES,
			order: { action: "ASC" },
		} as any,
		SMSTemplate,
	);
	const subscribeExecutorLocations = useModelSubscribe(
		{ revealExecutor: true },
		ExecutorLocations,
	);

	const canSubscribe: boolean = useMemo(
		() => connected && authorized,
		[connected, authorized],
	);

	// --------------------------------

	useLayoutEffect(() => {
		dispatch(getPolygons());
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, []);

	// --------------------------------

	const ratesModels = useMemo(
		() => (canSubscribe ? ratesSubscription.models : []),
		[canSubscribe, ratesSubscription.models],
	);

	useLayoutEffect(() => {
		dispatch(setRates(ratesModels));
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [ratesModels]);

	const smsTemplatesModels = useMemo(
		() => (canSubscribe ? smsTemplatesSubscription.models : []),
		[canSubscribe, smsTemplatesSubscription.models],
	);

	useLayoutEffect(() => {
		dispatch(orderPage.actions.setSMSTemplates(smsTemplatesModels));
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [smsTemplatesModels]);

	const executorLocationModels = useMemo(
		() => (canSubscribe ? subscribeExecutorLocations.models : []),
		[canSubscribe, subscribeExecutorLocations.models],
	);

	useLayoutEffect(() => {
		dispatch(
			globalState.executorLocations.data.setSubscriptionBasicData(
				executorLocationModels,
			),
		);
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [executorLocationModels]);

	const carModels = useMemo(
		() => (canSubscribe ? CarSub : []),
		[canSubscribe, CarSub],
	);

	useLayoutEffect(() => {
		dispatch(orders.map.data.setCarsData(carModels));
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [carModels]);

	return (
		<>
			<OrdersDataLoader carModels={carModels} />
			<ChatMessageLoader />
		</>
	);
};

declare namespace OrderPageModelsLoader {
	interface Props {}
}

export default OrderPageModelsLoader;
